import {useQuery} from '@tanstack/react-query'
import {useNavigate} from 'react-router-dom'

import {
  ProviderListSchemaResponse,
  ProviderResponse,
  ProviderSchemaResponse,
  Responses,
} from '../domain'
import {useProvider} from '../hook'

import {toast} from 'react-toastify'
import useRequestParams from 'src/feature/bite/hook/request'
import {API} from 'src/feature/bite/domain'
import {getTimeZone} from 'src/feature/bite/helpers/date'
import {useAuthUser} from 'react-auth-kit'

const endPoints = {
  Segment: 'GetMemberSegments',
  Address: 'GetMemberAddresses',
  Consent: 'GetMemberConcents',
  Transaction: 'GetMemberTransactions',
  Activities: 'GetMemberActivities',
  PushEngagement: 'GetMemberEngagements',
  EmailEngagement: 'GetMemberEngagements',
  SmsEngagement: 'GetMemberEngagements',
}

const typeOfFilterKey = {
  Segment: 'CreatedDate', // One Page
  Address: 'CreatedDate', // One Page
  Consent: 'CreatedDate', // One Page
  Transaction: 'Date',
  PushEngagement: 'CreatedOn',
  EmailEngagement: 'CreatedOn',
  SmsEngagement: 'CreatedOn',
}

export function useGetList() {
  const {auth, headers, errorMessage, signOut} = useRequestParams()
  const {listFilter, listFilterSet, listSet} = useProvider()
  const timeZoneOffset = getTimeZone()

  const userID = auth()?.id

  return useQuery(
    ['members', listFilter],
    async () => {
      const Response = await fetch(`${API}/Member/List`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          ...listFilter,
          userID,
          language: headers.Language,
          timeZoneOffSet: timeZoneOffset,
        }),
      })

      if (Response.status === 401) {
        signOut()
      }

      const Data = await Response.json()

      const parse = ProviderListSchemaResponse.parse(Data)

      listSet(parse.response)
      listFilterSet({
        ...listFilter,
        paging: {
          currentPageNumber: parse.response.paging.currentPageNumber,
          pageSize: parse.response.paging.pageSize,
        },
      })

      return true
    },
    {
      enabled: true,
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      refetchOnReconnect: false,
      retry: false,
      onSuccess: () => {},
      onError: (error) => {
        toast.error(errorMessage, {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: false,
          progress: undefined,
          theme: 'light',
        })
      },
    }
  )
}

export function useGetDetail() {
  const auth = useAuthUser()
  const router = useNavigate()
  const {detailSet} = useProvider()
  const {headers, errorMessage, signOut} = useRequestParams()
  const userID = auth()?.id
  const merchantID = auth()?.merchantID

  return useQuery(
    ['ProviderDetail'],
    async () => {
      if (!merchantID) return true
      const timeZoneOffset = getTimeZone()

      const Response = await fetch(`${API}/Provider/GetProvider`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          userID,
          language: headers.Language,
          merchantID,
          timeZoneOffSet: timeZoneOffset,
        }),
      })

      if (Response.status === 401) {
        signOut()
      }

      const response: ProviderResponse = await Response.json()

      const parse = ProviderSchemaResponse.parse(response)

      if (parse.responseResult.result === false) {
        toast.error(parse.responseResult.exceptionDetail || 'Error', {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: false,
          progress: undefined,
          theme: 'light',
        })
        return false
      }

      const detail = parse.response
      const responseData = parse.response

      const uniqueInfoContentDataNames = await responseData?.infoContentData?.reduce(
        async (accPromise, item) => {
          const acc = await accPromise
          const existing = acc.find((i) => i.name === item.name)
          if (!existing) {
            acc.push(item)
          }
          return acc
        },
        Promise.resolve([])
      )

      responseData.infoContentData = uniqueInfoContentDataNames

      // Start: Get HTML Items
      if (responseData?.infoContentData) {
        const faqs = [
          {
            title: 'Additional Information',
          },
        ]

        await Promise.all(
          responseData?.infoContentData?.map(async (item) => {
            const dataUrl = item?.contentURL
            if (dataUrl) {
              const res = await fetch(dataUrl, {
                method: 'GET',
                headers: {
                  'Content-Type': 'application/json',
                  Authorization: `Bearer ${auth()?.token}`,
                },
              })
              const dataValue = await res.text()
              const parser = new DOMParser()
              const doc = parser.parseFromString(dataValue, 'text/html')
              const contentView = doc.querySelector('#contentView')

              if (contentView) {
                item.data = contentView.innerHTML || ''
              }
            }
            return item
          })
        )

        await Promise.all(
          faqs.map((faq) => {
            const item = responseData?.infoContentData?.find((item) => item.name === faq.title)
            if (!item) {
              responseData?.infoContentData?.push({
                name: faq.title,
                data: '',
              })
            }
          })
        )
      }

      // End: Get HTML Items

      // Start: Get Additional Info

      if (detail?.additionalInformations) {
        const dataUrl = detail?.additionalInformations

        if (dataUrl) {
          const res = await fetch(dataUrl, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${auth()?.token}`,
            },
          })

          const dataValue = await res.text()
          const parser = new DOMParser()
          const doc = parser.parseFromString(dataValue, 'text/html')
          const contentView = doc.querySelector('#contentView')

          if (contentView) {
            detail.additionalInformations = contentView.innerHTML
          } else detail.additionalInformations = ''
        }
      }

      // End: Get Additional Info

      // Start: Set Working Hour List

      // set not existing days into workingHourList

      const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
      const workingHourList = detail.workingHourList

      days.forEach((day, index) => {
        // eslint-disable-next-line eqeqeq
        if (!workingHourList.find((item) => item.day == index)) {
          const indexStart = days.indexOf(day).toString()

          workingHourList.push({
            day: indexStart,
            workingStartHour: '',
            workingEndHour: '',
          })
        }
      })

      workingHourList.sort((a, b) => a.day - b.day)

      detail.workingHourList = workingHourList

      // End: Set Working Hour List

      detailSet(parse.response)
      return parse.response
    },
    {
      enabled: true,
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      refetchOnReconnect: false,
      retry: false,
      onSuccess: () => {},
      onError: (error) => {
        console.log('error', error)
        toast.error(errorMessage, {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: false,
          progress: undefined,
          theme: 'light',
        })
      },
    }
  )
}

export function useGetTabDetail(memberId: string) {
  const {detailTab, tabName, updateDetail, listFilterMarketing} = useProvider()
  const {headers, errorMessage, signOut} = useRequestParams()

  const apiEndPoint = endPoints[tabName]
  const filterKey = typeOfFilterKey[tabName]

  const defaultPagination = {currentPageNumber: 1, pageSize: 5}

  const paging =
    detailTab && detailTab[tabName]?.paging ? detailTab[tabName]?.paging : defaultPagination

  const filter = {
    filterList: listFilterMarketing.filterList,
    orderList: [{type: 2, key: filterKey}],
    paging: {currentPageNumber: paging.currentPageNumber, pageSize: paging.pageSize},
  }

  const timeZoneOffSet = getTimeZone()

  let engagementIndex: number, engagementName: string

  if (tabName === 'SmsEngagement') {
    engagementIndex = 1
    engagementName = 'textMessages'
  }
  if (tabName === 'PushEngagement') {
    engagementIndex = 3
    engagementName = 'pushes'
  }
  if (tabName === 'EmailEngagement') {
    engagementIndex = 2
    engagementName = 'emails'
  }

  const auth = useAuthUser()
  const userID = auth()?.id

  return useQuery(
    ['MemberDetailTab', tabName, filter],
    async () => {
      if (tabName == null || tabName === 'Overview' || tabName === 'Transaction') return true

      const Response = await fetch(`${API}/Member/${apiEndPoint}`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          memberId,
          userID,
          timeZoneOffSet,
          language: headers.Language,
          ...filter,
          ...(engagementIndex && {engagementType: engagementIndex}),
        }),
      })

      if (Response.status === 401) {
        signOut()
      }

      const Data = await Response.json()

      const ResponseSchema = Responses[tabName]
      const safeParse = ResponseSchema.safeParse(Data)

      if (!safeParse.success) {
        throw new Error(safeParse.error.message)
      }

      const parse = ResponseSchema.parse(Data)

      if (['SmsEngagement', 'PushEngagement', 'EmailEngagement'].includes(tabName)) {
        updateDetail(
          {
            dataList: parse.response[engagementName],
            paging: parse.response.listPaging,
          },
          tabName
        )
      } else {
        updateDetail(parse.response, tabName)
      }

      return true
    },
    {
      enabled: true,
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      refetchOnReconnect: false,
      retry: false,
      onSuccess: () => {},
      onError: (error: any) => {
        toast.error(errorMessage, {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: false,
          progress: undefined,
          theme: 'light',
        })
      },
    }
  )
}

export function useRenewMemberTransactions(memberId: string) {
  const {headers, signOut} = useRequestParams()
  const {detailTabSet, listFilterTrx} = useProvider()
  const auth = useAuthUser()
  const userID = auth()?.id

  const timeZoneOffSet = getTimeZone()

  const {data, isLoading, error, refetch} = useQuery(
    ['renewMemberTransactions', memberId, listFilterTrx],
    async () => {
      listFilterTrx.filterList = listFilterTrx.filterList.filter((item) => item.value?.[0] !== '')

      const response = await fetch(`${API}/Member/GetMemberTransactions`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          ...listFilterTrx,
          language: headers.Language,
          userID,
          timeZoneOffSet,
          memberId,
        }),
      })

      if (response.status === 401) {
        signOut()
      }

      const data = await response.json()
      const ResponseSchema = Responses['Transaction']
      const safeParse = ResponseSchema.safeParse(data)

      if (!safeParse.success) {
        throw new Error(safeParse.error.message)
      }

      const parse = ResponseSchema.parse(data)
      detailTabSet((prev) => ({
        ...prev,
        Transaction: parse.response,
      }))
      return true
    },
    {
      onSuccess: () => {},
      onError: (error) => {
        console.error('Error fetching transactions:', error) // Informative error handling
      },
      // Refetch data automatically when the 'handleSubmit' function is called
      refetchOnWindowFocus: false, // Avoid unnecessary refetches (optional)
      refetchInterval: 0, // Disable automatic refetching (optional)
    }
  )

  // Call refetch within 'handleSubmit' to manually trigger data refresh
  const handleRefresh = () => {
    refetch()
  }

  return {data, isLoading, error, handleRefresh}
}
